<template>
  <SearchFilterBar :store="store" :summary="filterSummary" placeholder="Search by order ID...">
    <div class="is-flex is-flex-wrap-wrap gap-2">
      <div class="field">
        <label class="label">Status</label>
        <MultiSelectCombobox
          :model-value="displayedStates"
          :items="stateOptions"
          placeholder="Filter by status..."
          @update:model-value="store.setFilter('states', $event)"
        >
          <template #selected-item="{ value, remove }">
            <PurchaseOrderState :state="value" removable @remove="remove()" />
          </template>
        </MultiSelectCombobox>
      </div>

      <div v-if="suppliers.length > 0" class="field">
        <label class="label">Supplier</label>
        <MultiSelectCombobox
          :model-value="store.supplierIds"
          :items="suppliers"
          placeholder="Filter by supplier..."
          @update:model-value="store.setFilter('supplierIds', $event)"
        />
      </div>

      <div v-if="transporters.length > 0" class="field">
        <label class="label">Transporter</label>
        <MultiSelectCombobox
          :model-value="store.transporterIds"
          :items="transporters"
          placeholder="Filter by transporter..."
          @update:model-value="store.setFilter('transporterIds', $event)"
        />
      </div>
    </div>

    <div class="field">
      <label class="label">Estimated Arrival</label>
      <div class="columns is-mobile is-flex-wrap-wrap">
        <div class="column">
          <input
            class="input"
            type="date"
            placeholder="From"
            :value="store.beginDate"
            @change="store.setFilter('beginDate', $event.target.value || null)"
          />
        </div>
        <div class="column">
          <input
            class="input"
            type="date"
            placeholder="To"
            :value="store.endDate"
            @change="store.setFilter('endDate', $event.target.value || null)"
          />
        </div>
      </div>
    </div>

    <div class="field">
      <label class="label">Received</label>
      <div class="columns is-mobile is-flex-wrap-wrap">
        <div class="column">
          <input
            class="input"
            type="date"
            placeholder="From"
            :value="store.receivedBeginDate"
            @change="store.setFilter('receivedBeginDate', $event.target.value || null)"
          />
        </div>
        <div class="column">
          <input
            class="input"
            type="date"
            placeholder="To"
            :value="store.receivedEndDate"
            @change="store.setFilter('receivedEndDate', $event.target.value || null)"
          />
        </div>
      </div>
    </div>
  </SearchFilterBar>
</template>

<script setup>
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { startCase } from "lodash-es";
  import { parseISO, format } from "date-fns";
  import MultiSelectCombobox from "./shared/multi-select-combobox.vue";
  import SearchFilterBar from "./shared/search-filter-bar.vue";
  import PurchaseOrderState from "./purchase-order-state.vue";
  import { usePurchaseOrderSearchStore } from "./stores/purchase-order-search-store";

  const stateOptions = [
    { value: "draft", label: "Draft" },
    { value: "active", label: "Active" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
  ];

  /* What the server falls back to when `states` is blank, mirrored here so the picker
   * and the summary show the states actually being listed. */
  const DEFAULT_STATES = ["draft", "active", "completed"];

  const props = defineProps({
    suppliers: { type: Array, default: () => [] },
    transporters: { type: Array, default: () => [] },
  });

  const store = usePurchaseOrderSearchStore();
  const { states } = storeToRefs(store);

  const displayedStates = computed(() => (states.value.length > 0 ? states.value : DEFAULT_STATES));

  const filterSummary = computed(() => {
    const parts = [`States: ${displayedStates.value.map(startCase).join(", ")}`];
    if (store.supplierIds.length > 0) {
      const names = store.supplierIds
        .map((id) => props.suppliers.find((s) => s.value === id)?.label)
        .filter(Boolean);
      if (names.length > 0) parts.push(`Suppliers: ${names.join(", ")}`);
    }
    if (store.transporterIds.length > 0) {
      const names = store.transporterIds
        .map((id) => props.transporters.find((t) => t.value === id)?.label)
        .filter(Boolean);
      if (names.length > 0) parts.push(`Transporters: ${names.join(", ")}`);
    }
    if (store.beginDate || store.endDate) {
      const from = store.beginDate ? format(parseISO(store.beginDate), "MMM d, yyyy") : "…";
      const to = store.endDate ? format(parseISO(store.endDate), "MMM d, yyyy") : "…";
      parts.push(`Arrival: ${from} – ${to}`);
    }
    if (store.receivedBeginDate || store.receivedEndDate) {
      const from = store.receivedBeginDate
        ? format(parseISO(store.receivedBeginDate), "MMM d, yyyy")
        : "…";
      const to = store.receivedEndDate
        ? format(parseISO(store.receivedEndDate), "MMM d, yyyy")
        : "…";
      parts.push(`Received: ${from} – ${to}`);
    }
    if (store.q) parts.unshift(`Searching: "${store.q}"`);
    return parts.join(" · ");
  });
</script>
